import { useNavigate } from 'react-router-dom';
import { useDashboardVendorRisks } from '@/api/hooks/useRiskAcceptances';
import { useDashboardOrgRisks } from '@/api/hooks/useOrgRiskAcceptances';

// Home Page BI — Risk Responses (ALWAYS ON, role-gated — NOT a licensed module, never filtered
// through entitledModules). Scoped to the signed-in user: two "waiting on you" queues whose counts
// are simply the lengths of their own lists. No portfolio-wide (global) count sits beside them, so two
// numbers can never disagree here. ⚠ RET-10: the Enterprise Risk Dashboard link is gone with the dashboard.
// ⓘ The registers are module-gated now (D-RR-SURFACES-MODULE-GATED) but these queues are NOT yet entitlement-aware:
//   a tenant without VRM / Compliance gets the api's 403 and an empty queue (accepted; B-HOME-RR-QUEUES-ENTITLEMENT).

type RrRow = { workflow_status: string; renewal_window_open?: boolean };

function noteFor(e: RrRow): { text: string; tone: string } {
  switch (e.workflow_status) {
    case 'returned':
      return { text: 'Returned to you', tone: '#D6584F' };
    case 'denied':
      return { text: 'Denied', tone: '#D6584F' };
    case 'expired':
      return { text: 'Expired', tone: '#D6584F' };
    case 'pending_approval':
      return { text: 'Awaiting your approval', tone: '#D89A3C' };
    case 'draft':
      return { text: 'Draft', tone: '#4FA8E8' };
    case 'approved':
      return e.renewal_window_open
        ? { text: 'Expiring soon', tone: '#D89A3C' }
        : { text: 'Approved', tone: '#4FA8E8' };
    default:
      return { text: e.workflow_status.replace(/_/g, ' '), tone: '#4FA8E8' };
  }
}

function QueueBox({
  label,
  entries,
  isLoading,
  linkLabel,
  linkPath,
  emptyText,
}: {
  label: string;
  entries: Array<{ id: string; title: string | null } & RrRow>;
  isLoading: boolean;
  linkLabel: string;
  linkPath: string;
  emptyText: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-[#24455F] bg-[#111E30]" data-testid="rr-queue">
      <div className="flex items-center gap-2 border-b border-[#1E3049] bg-[#0D1626] px-[14px] py-[10px]">
        <span className="text-[11.5px] font-semibold text-text-primary">{label}</span>
        <span className="ml-auto rounded-[9px] border border-[#255C8A] bg-[#132A44] px-[7px] text-[10.5px] font-bold tabular-nums text-[#8FCDFA]">
          {isLoading ? '·' : entries.length}
        </span>
      </div>
      {isLoading ? (
        <div className="px-[14px] py-[14px] text-[11.5px] text-text-secondary">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="px-[14px] py-[18px] text-center text-[11.5px] text-[#7A8CA3]">{emptyText}</div>
      ) : (
        entries.map((e) => {
          const note = noteFor(e);
          return (
            <button
              key={e.id}
              onClick={() => navigate(`${linkPath}/${e.id}`)}
              className="flex items-center gap-[10px] border-b border-[#1B2C43] px-[14px] py-[10px] text-left transition-colors last:border-b-0 hover:bg-[#1B2E48]"
              data-testid="rr-row"
            >
              <span className="flex-[0_0_3px] self-stretch rounded-[2px]" style={{ background: note.tone }} />
              <span className="flex-[1_1_auto] min-w-0 truncate text-[12px] text-[#D7E3F0]">{e.title ?? '—'}</span>
              <span className="flex-[0_0_auto] whitespace-nowrap text-[11px] font-semibold" style={{ color: note.tone }}>
                {note.text}
              </span>
            </button>
          );
        })
      )}
      <div className="px-[14px] py-[10px]">
        <button
          onClick={() => navigate(linkPath)}
          className="text-[11.5px] font-semibold text-accent-cyan transition-colors hover:text-text-primary"
        >
          {linkLabel}
        </button>
      </div>
    </div>
  );
}

export function RiskResponseHomeBand() {
  const { data: vendorRisks = [], isLoading: vLoading } = useDashboardVendorRisks();
  const { data: orgRisks = [], isLoading: oLoading } = useDashboardOrgRisks();

  return (
    <section className="flex flex-col gap-[9px]" data-testid="home-band-rr" aria-label="Risk responses requiring your action">
      <div className="flex flex-wrap items-center gap-[9px]">
        <span className="h-[15px] w-[3px] rounded-[2px]" style={{ background: '#A98FE0' }} />
        <span className="text-[15px] font-semibold tracking-[-0.01em] text-text-primary">Risk Responses Requiring Your Action</span>
      </div>
      <span className="text-[11px] leading-[1.5] text-[#7A8CA3] [text-wrap:pretty]">
        Risk responses assigned to you across vendor and organization workflows.
      </span>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
        <QueueBox
          label="Vendor Responses"
          entries={vendorRisks as Array<{ id: string; title: string | null } & RrRow>}
          isLoading={vLoading}
          linkLabel="View vendor risk responses →"
          linkPath="/risk-acceptances"
          emptyText="Nothing needs your action."
        />
        <QueueBox
          label="Organization Responses"
          entries={orgRisks as Array<{ id: string; title: string | null } & RrRow>}
          isLoading={oLoading}
          linkLabel="View organization risk responses →"
          linkPath="/org-risk-acceptances"
          emptyText="Nothing needs your action."
        />
      </div>
    </section>
  );
}
